import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const COLORES = ['#ff6600','#f87171','#4ade80','#38bdf8','#fbbf24','#a78bfa','#fb923c','#e879f9'];

export default function GeocercaManager({ tipo, entidadId, mapaInst, esAdmin, onClose }) {
  const apiBase = tipo === 'flota'
    ? '/api/v1/flota/geocercas'
    : `/api/v1/objetivos/${entidadId}/geocercas`;

  const [geocercas, setGeocercas]   = useState([]);
  const [dibujando, setDibujando]   = useState(false);
  const [nombre, setNombre]         = useState('');
  const [colorSel, setColorSel]     = useState(COLORES[0]);
  const [error, setError]           = useState('');

  const drawControlRef = useRef(null);
  const drawnLayerRef  = useRef(null);
  const geoLayersRef   = useRef({});

  const cargar = useCallback(async () => {
    try {
      const params = tipo === 'flota' && entidadId ? { vehiculo_id: entidadId } : {};
      const { data } = await axios.get(apiBase, { params });
      setGeocercas(data);
    } catch(_){}
  }, [apiBase, tipo, entidadId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Dibujar geocercas en el mapa
  useEffect(() => {
    if (!mapaInst?.current) return;
    const L = window.L;
    // Limpiar previas
    Object.values(geoLayersRef.current).forEach(l => mapaInst.current.removeLayer(l));
    geoLayersRef.current = {};

    geocercas.forEach(g => {
      try {
        const geo = typeof g.geojson === 'string' ? JSON.parse(g.geojson) : g.geojson;
        const layer = L.geoJSON(geo, {
          style: { color: g.color || '#ff6600', weight: 2, fillOpacity: 0.15 }
        });
        layer.bindTooltip(g.nombre, { sticky: true });
        layer.addTo(mapaInst.current);
        geoLayersRef.current[g.id] = layer;
      } catch(_){}
    });

    return () => {
      Object.values(geoLayersRef.current).forEach(l => {
        if (mapaInst.current) mapaInst.current.removeLayer(l);
      });
    };
  }, [geocercas, mapaInst]);

  const iniciarDibujo = () => {
    if (!mapaInst?.current || !window.L.Draw) return;
    setDibujando(true);
    setError('');
    const L = window.L;
    const drawnItems = new L.FeatureGroup();
    mapaInst.current.addLayer(drawnItems);
    drawnLayerRef.current = drawnItems;

    const dc = new L.Control.Draw({
      draw: {
        polygon: { shapeOptions: { color: colorSel, weight: 2, fillOpacity: 0.2 } },
        polyline: false, circle: false, circlemarker: false, marker: false, rectangle: true,
      },
      edit: { featureGroup: drawnItems },
    });
    mapaInst.current.addControl(dc);
    drawControlRef.current = dc;

    mapaInst.current.on(L.Draw.Event.CREATED, (e) => {
      drawnItems.addLayer(e.layer);
    });
  };

  const guardarGeocerca = async () => {
    if (!nombre.trim()) { setError('Escribe un nombre'); return; }
    if (!drawnLayerRef.current) { setError('Dibuja un polígono primero'); return; }

    const layers = [];
    drawnLayerRef.current.eachLayer(l => layers.push(l));
    if (!layers.length) { setError('Dibuja un polígono primero'); return; }

    const geojson = layers[0].toGeoJSON().geometry;

    try {
      if (tipo === 'flota') {
        await axios.post(apiBase, {
          nombre: nombre.trim(), geojson, color: colorSel,
          vehiculo_id: entidadId || null, tipo_alerta: 'ENTRADA_SALIDA'
        });
      } else {
        await axios.post(apiBase, {
          nombre: nombre.trim(), geojson: JSON.stringify(geojson),
          color: colorSel, tipo_alerta: 'ENTRADA_SALIDA'
        });
      }
      cancelarDibujo();
      setNombre('');
      cargar();
    } catch(e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const cancelarDibujo = () => {
    if (drawControlRef.current && mapaInst?.current) {
      mapaInst.current.removeControl(drawControlRef.current);
      drawControlRef.current = null;
    }
    if (drawnLayerRef.current && mapaInst?.current) {
      mapaInst.current.removeLayer(drawnLayerRef.current);
      drawnLayerRef.current = null;
    }
    setDibujando(false);
    setError('');
  };

  const eliminar = async (gid) => {
    if (!window.confirm('¿Eliminar esta geocerca?')) return;
    try {
      if (tipo === 'flota') await axios.delete(`/api/v1/flota/geocercas/${gid}`);
      else await axios.delete(`/api/v1/objetivos/${entidadId}/geocercas/${gid}`);
      cargar();
    } catch(_){}
  };

  const centrar = (g) => {
    const layer = geoLayersRef.current[g.id];
    if (layer && mapaInst?.current) {
      mapaInst.current.fitBounds(layer.getBounds(), { padding: [40,40] });
    }
  };

  const S = {
    panel: { display:'flex', flexDirection:'column', height:'100%', background:'#0b132b' },
    header: { padding:'10px 14px', borderBottom:'1px solid #1c2541', display:'flex', alignItems:'center', gap:8 },
    title: { fontWeight:700, color:'#ff6600', fontSize:13, flex:1 },
    close: { background:'none', border:'1px solid #1c2541', color:'#8892a4', borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:12 },
    body: { flex:1, overflow:'auto', padding:8 },
    item: { background:'#04080f', border:'1px solid #1c2541', borderRadius:8, padding:10, marginBottom:6, cursor:'pointer' },
    row: { display:'flex', alignItems:'center', gap:8 },
    dot: (c) => ({ width:12, height:12, borderRadius:'50%', background: c || '#ff6600', flexShrink:0 }),
    name: { fontWeight:600, fontSize:12, color:'#e0e1dd', flex:1 },
    tipo: { fontSize:10, color:'#8892a4' },
    delBtn: { background:'none', border:'none', color:'#f87171', cursor:'pointer', fontSize:14 },
    form: { padding:'10px 14px', borderTop:'1px solid #1c2541' },
    inp: { width:'100%', background:'#04080f', border:'1px solid #1c2541', color:'#e0e1dd', borderRadius:5, padding:'5px 8px', fontSize:12 },
    btn: { background:'#ff6600', border:'none', color:'#fff', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:700 },
    btnSm: { background:'#1c2541', border:'none', color:'#8892a4', borderRadius:5, padding:'4px 10px', cursor:'pointer', fontSize:11 },
  };

  return (
    <div style={S.panel}>
      <div style={S.header}>
        <span style={S.title}>🔲 Geocercas</span>
        <button style={S.close} onClick={() => { cancelarDibujo(); onClose(); }}>✕ Cerrar</button>
      </div>

      <div style={S.body}>
        {geocercas.length === 0 && !dibujando && (
          <div style={{ color:'#8892a4', textAlign:'center', padding:20, fontSize:12 }}>
            Sin geocercas activas
          </div>
        )}
        {geocercas.map(g => (
          <div key={g.id} style={S.item} onClick={() => centrar(g)}>
            <div style={S.row}>
              <div style={S.dot(g.color)} />
              <span style={S.name}>{g.nombre}</span>
              <span style={S.tipo}>{g.tipo_alerta}</span>
              {esAdmin && <button style={S.delBtn} onClick={(e) => { e.stopPropagation(); eliminar(g.id); }}>✕</button>}
            </div>
          </div>
        ))}
      </div>

      {dibujando && (
        <div style={S.form}>
          <div style={{ marginBottom:8, fontSize:11, color:'#4ade80' }}>
            Usa la barra de herramientas del mapa para dibujar un polígono o rectángulo
          </div>
          <input placeholder="Nombre de la geocerca" value={nombre}
                 onChange={e => setNombre(e.target.value)} style={{...S.inp, marginBottom:8}} />
          <div style={{ display:'flex', gap:4, marginBottom:8 }}>
            {COLORES.map(c => (
              <div key={c} onClick={() => setColorSel(c)} style={{
                width:20, height:20, borderRadius:4, background:c, cursor:'pointer',
                border: colorSel === c ? '2px solid #fff' : '2px solid transparent',
              }} />
            ))}
          </div>
          {error && <div style={{ color:'#f87171', fontSize:11, marginBottom:6 }}>{error}</div>}
          <div style={{ display:'flex', gap:8 }}>
            <button style={S.btn} onClick={guardarGeocerca}>💾 Guardar</button>
            <button style={S.btnSm} onClick={cancelarDibujo}>Cancelar</button>
          </div>
        </div>
      )}

      {!dibujando && esAdmin && (
        <div style={{ padding:'8px 14px', borderTop:'1px solid #1c2541' }}>
          <button style={S.btn} onClick={iniciarDibujo}>+ Nueva geocerca</button>
        </div>
      )}
    </div>
  );
}
